import type { DealCard } from "../types";

export type CardTone = "danger" | "warning" | "ok" | "muted";

export type CardStatus = {
  /** i18n key under crm.funnel.status */
  key: "no_call" | "overdue" | "today" | "tomorrow" | "later" | "booked_until";
  tone: CardTone;
  /** Interpolation values for the key */
  values: Record<string, string | number>;
};

/** Minutes a new lead may wait for a call before its card turns red */
export const NO_CALL_ALERT_MINUTES = 10;

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const formatTime = (date: Date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

export const formatDayMonth = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}`;

/** Elapsed time in the coarsest useful unit: minutes, hours or days */
export const elapsed = (from: Date, now: Date) => {
  const minutes = Math.max(
    0,
    Math.floor((now.getTime() - from.getTime()) / MINUTE),
  );
  if (minutes < 60) return { count: minutes, unit: "minutes" as const };
  if (minutes < 24 * 60)
    return { count: Math.floor(minutes / 60), unit: "hours" as const };
  return { count: Math.floor(minutes / (24 * 60)), unit: "days" as const };
};

/**
 * What the funnel card says under the lead (docs/design/Funnel.dc.html), most
 * urgent first: a new lead nobody called, an overdue next action, a booking
 * deadline, the next planned action.
 */
export const getCardStatus = (
  card: Pick<
    DealCard,
    | "stage"
    | "lead_created_at"
    | "created_at"
    | "first_action_at"
    | "last_call_at"
    | "next_task_due"
    | "next_task_type"
    | "booked_until"
  >,
  now: Date,
): CardStatus | null => {
  if (card.stage === "new" && !card.last_call_at && !card.first_action_at) {
    const since = new Date(card.lead_created_at ?? card.created_at);
    const waited = elapsed(since, now);
    const minutes = (now.getTime() - since.getTime()) / MINUTE;
    return {
      key: "no_call",
      tone: minutes >= NO_CALL_ALERT_MINUTES ? "danger" : "warning",
      values: { count: waited.count, unit: waited.unit },
    };
  }

  const due = card.next_task_due ? new Date(card.next_task_due) : null;
  if (due && due.getTime() < now.getTime()) {
    const late = elapsed(due, now);
    return {
      key: "overdue",
      tone: "danger",
      values: { count: late.count, unit: late.unit },
    };
  }

  if (card.stage === "booking" && card.booked_until) {
    return {
      key: "booked_until",
      tone: "warning",
      values: { date: formatDayMonth(new Date(card.booked_until)) },
    };
  }

  if (due) {
    const days = Math.round((startOfDay(due) - startOfDay(now)) / DAY);
    const task = card.next_task_type ?? "none";
    if (days === 0)
      return {
        key: "today",
        tone: "ok",
        values: { task, time: formatTime(due) },
      };
    if (days === 1)
      return {
        key: "tomorrow",
        tone: "ok",
        values: { task, time: formatTime(due) },
      };
    return {
      key: "later",
      tone: "muted",
      values: { task, date: formatDayMonth(due) },
    };
  }

  return null;
};

/** Whole days the deal has been in its current stage */
export const daysInStage = (stageChangedAt: string | undefined, now: Date) =>
  stageChangedAt
    ? Math.max(
        0,
        Math.floor((now.getTime() - new Date(stageChangedAt).getTime()) / DAY),
      )
    : 0;

/** Working hours of the sales team, Kyiv time: Monday to Saturday, 9:00–20:00 */
export const isWorkingTime = (now: Date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Kyiv",
    weekday: "short",
    hour: "numeric",
    hourCycle: "h23",
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === "weekday")?.value;
  const hour = Number(parts.find((p) => p.type === "hour")?.value);
  return weekday !== "Sun" && hour >= 9 && hour < 20;
};

/** A new lead nobody touched for 15 minutes during working hours: light it up */
export const needsActionNow = (
  card: {
    stage: string;
    first_action_at?: string | null;
    lead_created_at?: string | null;
  },
  now: Date,
) =>
  card.stage === "new" &&
  !card.first_action_at &&
  !!card.lead_created_at &&
  now.getTime() - Date.parse(card.lead_created_at) > 15 * 60_000 &&
  isWorkingTime(now);
